import React, { useMemo } from 'react';
import styled from 'styled-components';
import DotLoader from '@svg/dot_loader';

import { Code } from '@common/Code';
import { NodeTrace, tabActions } from '@store/slices';
import { NodeIcon, NodeId, getIcon } from '../Defaults';
import { Alert, Collapse, IconButton, Stack, alpha, capitalize } from '@mui/material';
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '@store/index';
import { useTheme } from '../../../styles';
import { useParams } from 'react-router-dom';
import { Tooltip } from '@common/Tooltip';

export const CardContainer = styled.div<{ allowModule?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line.light};
  width: 100%;
  white-space: pre-wrap;

  background-color: ${({ allowModule, theme }) => (allowModule ? alpha(theme.telnyx.yellow, 0.08) : 'transparent')};

  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 14px;
    font-family: 'Inter';
    margin-bottom: 4px;
  }

  a {
    color: ${({ theme }) => theme.primary};
  }
`;

export const CardHeader = styled.div<{ collapsed?: boolean }>`
  display: flex;
  flex-direction: column;

  padding: 12px;

  h4 {
    color: ${({ theme }) => theme.text.standard};
    font-family: 'PPFormula';
    font-size: 12px;
    padding-top: 4px;
    font-weight: 600;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 28px;
    max-height: 28px;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

export const CardContent = styled.div<{ allowModule?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  color: ${({ theme }) => theme.text.standard};
  background-color: ${({ allowModule, theme }) => (allowModule ? alpha(theme.telnyx.yellow, 0.1) : 'transparent')};

  && ::-webkit-scrollbar {
    width: 14px;
    height: 14px;
  }

  && ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.scrollbar.track};
  }

  && ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.scrollbar.thumb};
  }
`;

export const ErrorContainer = styled.div`
  display: flex;
  overflow-x: auto;
`;

const ModuleTraces = styled.div<{ collapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 12px 12px;
  padding-top: ${({ collapsed }) => (collapsed ? '4px' : '8px')};
  gap: 12px;
`;

const formatMilliseconds = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const remainingMilliseconds = milliseconds % 1000;

  const formattedTime = [];
  if (hours > 0) formattedTime.push(`${hours}h`);
  if (minutes > 0) formattedTime.push(`${minutes}m`);

  // Show milliseconds only if seconds are 0
  if (seconds > 0) {
    formattedTime.push(`${seconds}s`);
  } else if (remainingMilliseconds > 0) {
    formattedTime.push(`${remainingMilliseconds.toFixed(0)}ms`);
  }

  return formattedTime.join(' ') || '0ms';
};

interface Props {
  trace: NodeTrace;
  allowModule?: boolean;
  moduleTraces?: NodeTrace[];
}

export const WorkflowTrace: React.FC<Props> = ({ trace, allowModule = false, moduleTraces = [] }) => {
  const dispatch = useDispatch();
  const run = useSelector((state: State) => state.tabs.active.run);

  const { workflow_id } = useParams();

  const commands = useSelector((state: State) => state.tabs.active.commands) || [];
  const command = commands?.find((command) => command?.node?.id === trace?.metadata?.node_id);
  const modules = useSelector((state: State) => state.tabs.active.modules) || [];

  const isModNode = !allowModule && command?.config?.command_id === 'node.module';
  const modWorkflow = isModNode ? modules?.find((mod) => mod?.flow?.flow_id === command?.state?.moduleId) : undefined;
  const modName = !allowModule ? modWorkflow?.label : undefined;

  const isWorkflowError = trace?.type === 'workflow_error' && run?.error;
  const isTraceError = trace?.type?.includes('error');
  const nodeHasContent =
    isTraceError || trace?.description || trace?.metadata?.output || trace?.metadata?.status === 'failed';
  const isError = trace.type.includes('workflow') || trace.type.includes('error');

  const max_jumps = trace?.metadata?.max_jumps;
  const jump_increment = trace?.metadata?.jump_increment;
  const hasJumpIncrement = Boolean(max_jumps !== undefined && jump_increment !== undefined);

  const isCollapsedDisabled = useMemo(
    () => trace?.metadata?.workflow_id !== workflow_id && trace?.metadata?.status === 'failed',
    [trace, workflow_id]
  );

  const getWorkflowMessage = useMemo(() => {
    const status = trace?.metadata?.status;
    if (status === 'processing')
      return 'This node is currently running. You will see the data output when it is finished.';
    if (status === 'pending' || !status) return 'This node has not been executed yet.';
    return 'This node does not have output data.';
  }, [trace]);

  return (
    <CardContainer allowModule={allowModule}>
      <CardHeader collapsed={trace?.collapsed}>
        <div className='row'>
          <div className='title'>
            <NodeIcon timer background={trace?.background}>
              {getIcon(trace.icon)}
            </NodeIcon>
            <h4>{modName || trace.name}</h4>
          </div>
          <Stack direction='row' spacing={1.5} alignItems='center'>
            {hasJumpIncrement && (
              <Tooltip
                sx={{ maxWidth: '300px' }}
                title='This indicates how many times this node has jumped to the target, and how many times it can jump to the target (jumps/max_jumps). A jump will reset the node events downstream from the target, but you can view all the events in the Reporting tab.'
                placement='top'
                arrow
              >
                <NodeId timer>
                  {jump_increment}/{max_jumps}
                </NodeId>
              </Tooltip>
            )}
            {!isError && <NodeDuration trace={trace} />}
            {trace?.metadata?.node_id && <NodeId background={trace?.background}>{`#${trace.metadata.node_id}`}</NodeId>}
            {trace?.icon !== 'workflow' && (
              <IconButton
                size='small'
                sx={{ padding: '2px', borderRadius: '6px' }}
                disabled={isCollapsedDisabled}
                onClick={() =>
                  dispatch(
                    tabActions.setTraceCollapse({
                      node_id: trace?.metadata?.node_id || '',
                      workflow_id: trace?.metadata?.workflow_id || '',
                      collapsed: !trace.collapsed,
                    })
                  )
                }
              >
                {!trace.collapsed ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
              </IconButton>
            )}
          </Stack>
        </div>
      </CardHeader>
      {isWorkflowError && (
        <Stack padding='0 12px 12px 12px'>
          <Alert severity='error'>{run?.error || 'An unexpected error occured.'}</Alert>
        </Stack>
      )}
      <Collapse in={!trace.collapsed} timeout='auto' unmountOnExit>
        {nodeHasContent && <TraceNodeOutput trace={trace} />}
        {!nodeHasContent && trace?.metadata?.status && (
          <Stack padding='0 12px 12px 12px'>
            <Alert severity='info'>{getWorkflowMessage}</Alert>
          </Stack>
        )}
      </Collapse>
      {moduleTraces.length > 0 && (
        <ModuleTraces collapsed={trace?.collapsed}>
          {moduleTraces.map((moduleTrace, index) => (
            <WorkflowTrace
              key={`${trace?.metadata?.workflow_id}/${trace?.metadata?.node_id}/${index}`}
              trace={moduleTrace}
              allowModule
            />
          ))}
        </ModuleTraces>
      )}
    </CardContainer>
  );
};

const NodeDuration: React.FC<{ trace: NodeTrace }> = ({ trace }) => {
  const theme = useTheme();

  if (trace?.metadata?.status === 'pending') {
    return (
      <NodeId timer background={theme.telnyx.purple}>
        -
      </NodeId>
    );
  }

  if (trace?.metadata?.status === 'failed' || trace?.metadata?.status === 'cancelled') {
    return (
      <NodeId timer background={theme.telnyx.purple}>
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            color: trace?.metadata?.status === 'failed' ? theme.telnyx.error : theme.telnyx.yellow,
          }}
        >
          {capitalize(trace?.metadata?.status)}
        </p>
      </NodeId>
    );
  }

  if (trace?.metadata?.duration) {
    return (
      <NodeId timer background={theme.telnyx.purple}>
        {formatMilliseconds(trace.metadata.duration)}
      </NodeId>
    );
  }

  return (
    <NodeId timer background={theme.telnyx.purple}>
      <DotLoader color={theme.primary} height={5} />
    </NodeId>
  );
};

interface NodeOutputProps {
  trace: NodeTrace;
  allowModule?: boolean;
}

const TraceNodeOutput: React.FC<NodeOutputProps> = ({ trace, allowModule }) => {
  const theme = useTheme();
  const { workflow_id } = useParams();

  const isTraceError = trace?.metadata?.status === 'failed';
  const moduleColour = alpha(theme.telnyx.yellow, 0.08);
  const nodeErrors = useSelector((state: State) => state.tabs.active.nodeErrors) || [];

  const commands = useSelector((state: State) => state.tabs.active.commands) || [];
  const command = commands?.find(
    (command) =>
      command?.node?.id === trace?.metadata?.node_id &&
      ((allowModule && trace?.metadata?.workflow_id !== workflow_id) ||
        (!allowModule && trace?.metadata?.workflow_id === workflow_id))
  );
  const isModNode = !allowModule && command?.config?.command_id === 'node.module';

  const getNodeError = () => {
    const commandError = nodeErrors.find(
      (node) => node.workflowId === trace?.metadata?.workflow_id && node.id === trace?.metadata?.node_id
    );

    const commandErrorFields = commandError?.fields || [];

    if (commandErrorFields.length === 0) {
      if (!commandError?.error?.message) return undefined;
      return isModNode
        ? `A node in this module failed to execute with error: ${commandError?.error?.message}`
        : `This node failed to execute with error: ${commandError?.error?.message}`;
    }

    const fieldErrorsParsed = [
      isModNode
        ? `A node in this module failed to execute and returned errors related to the configuration.`
        : `This node failed to execute and returned errors related to the configuration:\n`,
    ];

    if (!isModNode) {
      for (const fieldError of commandErrorFields) {
        fieldErrorsParsed.push(`- ${fieldError.id}: ${fieldError.message}`);
      }
    }

    return fieldErrorsParsed.join('\n');
  };

  const error = getNodeError();

  if (error) {
    return (
      <Stack padding='0 12px 12px 12px'>
        <Alert severity='error'>{error}</Alert>
      </Stack>
    );
  }

  // if the node has an error
  if (isTraceError) {
    if (!trace?.description) return null;

    return (
      <CardContent allowModule={allowModule}>
        <ErrorContainer>
          <Code
            noPadding
            code={trace.description}
            language='json'
            backgroundColor={allowModule ? moduleColour : undefined}
          />
        </ErrorContainer>
      </CardContent>
    );
  }

  // if the node has output data
  if (trace?.metadata?.output) {
    return (
      <CardContent allowModule={allowModule}>
        <ErrorContainer>
          <Code
            noPadding
            code={trace.metadata.output}
            language='json'
            backgroundColor={allowModule ? moduleColour : undefined}
          />
        </ErrorContainer>
      </CardContent>
    );
  }

  // if the node has no output and no error
  return null;
};
